import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, X, Save, Download, Copy, Palette, Type, Ruler, FileText,
  Loader2, Check, RotateCcw, AlertTriangle,
} from 'lucide-react';
import { API } from '../utils/apiBase';
import { buildStyledHTML } from '../utils/resumeHtml';

// Control panel sections are collapsible — keeps the preview real-estate
// dominant (which is where users want to spend their attention).
const SECTIONS = [
  { id: 'theme', label: 'Theme', icon: Palette },
  { id: 'typography', label: 'Typography', icon: Type },
  { id: 'layout', label: 'Layout', icon: Ruler },
];

const ACCENT_PRESETS = [
  '#0f1115', '#1a2f4b', '#002fa7', '#1e3a2f', '#b45309',
  '#0f766e', '#7c2d12', '#581c87', '#b91c1c', '#4d7c0f',
];

const BODY_FONTS = [
  { value: null, label: 'Default (theme)' },
  { value: "'Inter', sans-serif", label: 'Inter' },
  { value: "'Source Sans Pro', sans-serif", label: 'Source Sans Pro' },
  { value: "'IBM Plex Sans', sans-serif", label: 'IBM Plex Sans' },
  { value: "'Georgia', serif", label: 'Georgia' },
  { value: "'EB Garamond', Georgia, serif", label: 'EB Garamond' },
];

const HEADING_FONTS = [
  { value: null, label: 'Default (theme)' },
  { value: "'Inter', sans-serif", label: 'Inter' },
  { value: "'Georgia', serif", label: 'Georgia' },
  { value: "'Playfair Display', serif", label: 'Playfair Display' },
  { value: "'EB Garamond', Georgia, serif", label: 'EB Garamond' },
  { value: "'IBM Plex Sans', sans-serif", label: 'IBM Plex Sans' },
];

const Swatch = ({ color, selected, onClick, label }) => (
  <button
    type="button"
    title={label || color}
    onClick={onClick}
    className={`w-7 h-7 rounded-sm ring-offset-2 transition-all ${selected ? 'ring-2 ring-[#002fa7]' : 'ring-1 ring-[#e5e7eb] hover:ring-[#9ca3af]'}`}
    style={{ background: color }}
    data-testid={`swatch-${color}`}
    aria-label={label || color}
  />
);

const Segmented = ({ value, options, onChange, testIdPrefix }) => (
  <div className="inline-flex border border-[#e5e7eb] bg-white p-0.5 w-full">
    {options.map((opt) => (
      <button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        className={`flex-1 text-xs px-2 py-1.5 font-medium transition-colors ${
          value === opt.value ? 'bg-[#0f1115] text-white' : 'text-[#4b5563] hover:text-[#0f1115]'
        }`}
        data-testid={`${testIdPrefix}-${opt.value}`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const ResumeStudio = ({ resumeId: propResumeId, onClose }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = propResumeId || params.id;
  const asModal = !!onClose;
  const [resume, setResume] = useState(null);
  const [style, setStyle] = useState(null);
  const [cssVars, setCssVars] = useState('');
  const [themes, setThemes] = useState([]);
  const [content, setContent] = useState('');
  const [openSection, setOpenSection] = useState('theme');
  const [savingStyle, setSavingStyle] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [copied, setCopied] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const autosaveTimer = useRef(null);
  const previewFrame = useRef(null);
  const dirtyRef = useRef(false);
  // Track the current content without re-creating handlers (sidesteps the
  // classic useCallback stale-closure trap that caused autosave to persist
  // the pre-fetch empty string and wipe the resume).
  const contentRef = useRef('');
  // Require a real user keystroke inside an editable region before any save
  // can fire. Prevents phantom focusout → serialize → save on mount / iframe
  // reloads / clicking sidebar controls.
  const userTypedRef = useRef(false);

  useEffect(() => { contentRef.current = content; }, [content]);

  // --- Bootstrap: load resume, theme catalog, style ----------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [resumeRes, themesRes, styleRes] = await Promise.all([
          axios.get(`${API}/resumes/${id}`, { withCredentials: true }),
          axios.get(`${API}/resumes/style/themes`),
          axios.get(`${API}/resumes/${id}/style`, { withCredentials: true }),
        ]);
        if (cancelled) return;
        setResume(resumeRes.data);
        setContent(resumeRes.data.content || '');
        setThemes(themesRes.data.themes);
        setStyle(styleRes.data.style);
        setCssVars(styleRes.data.css_vars);
      } catch (err) {
        toast.error('Could not load resume');
        if (asModal) onClose?.();
        else navigate('/dashboard');
      } finally {
        if (!cancelled) setInitialLoad(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate, asModal, onClose]);

  // --- Preview HTML (re-derived on style/content change) -----------------
  const previewHtml = useMemo(() => {
    if (!cssVars) return '';
    return buildStyledHTML({
      content,
      cssVars,
      editable: true,
      pageSize: style?.page_size || 'letter',
    });
  }, [cssVars, content, style]);

  // --- Hook into iframe's contentEditable edits --------------------------
  // SAFETY: only save when the user has actually typed (input event fired)
  // inside an editable region during this focus session, AND the serialized
  // content is non-empty AND actually differs from what was loaded. This
  // prevents the "open modal → close → resume wiped" data-loss bug caused
  // by phantom focusout events triggering lossy serialize+save cycles.
  useEffect(() => {
    const iframe = previewFrame.current;
    if (!iframe) return;
    const onLoad = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;

      doc.addEventListener('input', (e) => {
        const t = e.target;
        if (t && t.getAttribute?.('contenteditable') === 'true') {
          userTypedRef.current = true;
        }
      });

      doc.addEventListener('focusout', (e) => {
        const target = e.target;
        if (!target || target.getAttribute?.('contenteditable') !== 'true') return;
        if (!userTypedRef.current) return; // ← critical: no typing → no save
        userTypedRef.current = false;

        const paper = doc.querySelector('.paper');
        if (!paper) return;
        const md = serializePreviewToMarkdown(paper);
        // Never persist empty or whitespace-only content. And don't save
        // if the new markdown is identical to the version we loaded.
        if (!md || !md.trim()) return;
        if (md === contentRef.current) return;

        setContent(md);
        contentRef.current = md;
        dirtyRef.current = true;
        scheduleAutosave();
      });
    };
    iframe.addEventListener('load', onLoad);
    return () => iframe.removeEventListener('load', onLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewHtml]);

  // --- Autosave with 1.5s debounce --------------------------------------
  const scheduleAutosave = useCallback(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      saveContent();
    }, 1500);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveContent = useCallback(async () => {
    if (!dirtyRef.current) return;
    const toSave = contentRef.current;
    if (!toSave || !toSave.trim()) return; // ← belt-and-braces empty guard
    setSavingContent(true);
    try {
      await axios.put(
        `${API}/resumes/${id}/content`,
        { content: toSave },
        { withCredentials: true }
      );
      setLastSavedAt(new Date());
      dirtyRef.current = false;
    } catch (err) {
      toast.error('Could not save changes');
    } finally {
      setSavingContent(false);
    }
  }, [id]);

  // --- Style mutations ---------------------------------------------------
  const patchStyle = useCallback(async (patch) => {
    setSavingStyle(true);
    try {
      const res = await axios.put(
        `${API}/resumes/${id}/style`,
        patch,
        { withCredentials: true }
      );
      setStyle(res.data.style);
      setCssVars(res.data.css_vars);
      setLastSavedAt(new Date());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Style update failed');
    } finally {
      setSavingStyle(false);
    }
  }, [id]);

  const resetStyle = useCallback(() => {
    patchStyle({
      theme: 'classic',
      font_family: null,
      heading_font: null,
      font_size: 'medium',
      spacing: 'comfortable',
      margin: 'normal',
      page_size: 'letter',
      accent_override: null,
    });
  }, [patchStyle]);

  // --- Download / copy ---------------------------------------------------
  const download = useCallback(async (fmt) => {
    setDownloading(fmt);
    try {
      // Reuse the live preview HTML — guarantees 1:1 with what the user sees.
      const res = await axios.post(
        `${API}/resumes/${id}/export`,
        { fmt, html: previewHtml },
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = res.data;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${(resume?.title || 'resume').replace(/[^a-z0-9]+/gi, '_')}.${fmt}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(`Download ${fmt.toUpperCase()} failed`);
    } finally {
      setDownloading(null);
    }
  }, [id, previewHtml, resume]);

  const copyToClipboard = useCallback(async () => {
    try {
      // Copy the FULL html document (including the <style> block) so Word /
      // Outlook / Gmail preserve theme fonts, colors, and spacing on paste.
      // The earlier approach copied only `.paper.outerHTML` which stripped all
      // theme CSS and produced a mashed-together header on paste.
      const doc = previewFrame.current?.contentDocument;
      const paper = doc?.querySelector('.paper');
      if (!doc || !paper) throw new Error('not ready');
      const styleEls = Array.from(doc.querySelectorAll('style'))
        .map((el) => el.outerHTML)
        .join('');
      const htmlText = `<!doctype html><html><head><meta charset="utf-8">${styleEls}</head><body>${paper.outerHTML}</body></html>`;
      const plain = paper.innerText;
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([htmlText], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Copy failed — your browser may not support rich clipboard');
    }
  }, []);

  // --- Render --------------------------------------------------------------

  if (initialLoad) {
    return (
      <div className={asModal ? 'fixed inset-0 z-50 bg-white flex items-center justify-center' : 'min-h-screen flex items-center justify-center'}>
        <Loader2 size={36} className="animate-spin text-[#002fa7]" />
      </div>
    );
  }
  if (!resume || !style) return null;

  const savedLabel = !lastSavedAt
    ? 'Not saved yet'
    : savingContent || savingStyle
      ? 'Saving…'
      : `Saved ${timeAgo(lastSavedAt)}`;

  return (
    <div
      className={
        asModal
          ? 'fixed inset-0 z-50 bg-[#f4f5f7] flex flex-col'
          : 'min-h-screen bg-[#f4f5f7] flex flex-col'
      }
      data-testid="resume-studio"
    >
      {/* Top bar */}
      <header className="bg-white border-b border-[#e5e7eb] px-4 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button
            onClick={() => (asModal ? onClose?.() : navigate(`/resume/${id}`))}
            className="text-[#6b7280] hover:text-[#0f1115]"
            title={asModal ? 'Close Studio' : 'Back to resume'}
            data-testid="studio-back"
          >
            {asModal ? <X size={22} /> : <ArrowLeft size={20} />}
          </button>
          <div>
            <div className="text-xs text-[#6b7280] uppercase tracking-wider">Resume Studio</div>
            <div className="text-sm font-semibold text-[#0f1115]">{resume.title || 'Untitled Resume'}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#6b7280] flex items-center gap-1.5" data-testid="save-status">
            {savingContent || savingStyle ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} className="text-[#10b981]" />}
            {savedLabel}
          </span>
          <button onClick={copyToClipboard} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-[#e5e7eb] hover:bg-[#f4f5f7]" data-testid="studio-copy">
            {copied ? <Check size={14} className="text-[#10b981]" /> : <Copy size={14} />} Copy
          </button>
          <button onClick={() => download('docx')} disabled={downloading} className="flex items-center gap-2 px-3 py-1.5 text-sm border border-[#e5e7eb] hover:bg-[#f4f5f7] disabled:opacity-50" data-testid="studio-docx">
            {downloading === 'docx' ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} DOCX
          </button>
          <button onClick={() => download('pdf')} disabled={downloading} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-[#002fa7] text-white hover:bg-[#001f70] disabled:opacity-50" data-testid="studio-pdf">
            {downloading === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} PDF
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Control panel */}
        <aside className="w-full lg:w-80 bg-white border-r border-[#e5e7eb] overflow-y-auto" data-testid="studio-controls">
          {SECTIONS.map((section) => (
            <div key={section.id} className="border-b border-[#e5e7eb]">
              <button
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#f9fafb]"
                onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
                data-testid={`control-toggle-${section.id}`}
              >
                <section.icon size={16} className="text-[#4b5563]" />
                <span className="font-semibold text-sm text-[#0f1115] flex-1">{section.label}</span>
                <span className="text-xs text-[#9ca3af]">{openSection === section.id ? '−' : '+'}</span>
              </button>
              {openSection === section.id && (
                <div className="px-4 pb-4 pt-1 space-y-4">
                  {section.id === 'theme' && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        {themes.map((t) => (
                          <button
                            key={t.key}
                            onClick={() => patchStyle({ theme: t.key, accent_override: null })}
                            className={`p-2 border text-left transition-colors ${style.theme === t.key ? 'border-[#002fa7] bg-[#eff6ff]' : 'border-[#e5e7eb] hover:border-[#9ca3af]'}`}
                            data-testid={`theme-${t.key}`}
                          >
                            <div className="w-full h-4 rounded-sm mb-1" style={{ background: t.accent }} />
                            <div className="text-xs font-semibold text-[#0f1115]">{t.label}</div>
                            <div className="text-[10px] text-[#6b7280]">{t.industry}</div>
                          </button>
                        ))}
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-[#4b5563] mb-2 uppercase tracking-wider">Custom accent</div>
                        <div className="flex flex-wrap gap-1.5">
                          {ACCENT_PRESETS.map((c) => (
                            <Swatch
                              key={c}
                              color={c}
                              selected={style.accent_override === c}
                              onClick={() => patchStyle({ accent_override: c })}
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  {section.id === 'typography' && (
                    <>
                      <LabelField label="Heading font">
                        <Select
                          value={style.heading_font}
                          options={HEADING_FONTS}
                          onChange={(v) => patchStyle({ heading_font: v })}
                          testId="heading-font"
                        />
                      </LabelField>
                      <LabelField label="Body font">
                        <Select
                          value={style.font_family}
                          options={BODY_FONTS}
                          onChange={(v) => patchStyle({ font_family: v })}
                          testId="body-font"
                        />
                      </LabelField>
                      <LabelField label="Font size">
                        <Segmented
                          value={style.font_size}
                          options={[{ value: 'small', label: 'Small' }, { value: 'medium', label: 'Medium' }, { value: 'large', label: 'Large' }]}
                          onChange={(v) => patchStyle({ font_size: v })}
                          testIdPrefix="font-size"
                        />
                      </LabelField>
                    </>
                  )}
                  {section.id === 'layout' && (
                    <>
                      <LabelField label="Spacing">
                        <Segmented
                          value={style.spacing}
                          options={[{ value: 'compact', label: 'Compact' }, { value: 'comfortable', label: 'Comfy' }, { value: 'spacious', label: 'Spacious' }]}
                          onChange={(v) => patchStyle({ spacing: v })}
                          testIdPrefix="spacing"
                        />
                      </LabelField>
                      <LabelField label="Margin">
                        <Segmented
                          value={style.margin}
                          options={[{ value: 'narrow', label: 'Narrow' }, { value: 'normal', label: 'Normal' }, { value: 'wide', label: 'Wide' }]}
                          onChange={(v) => patchStyle({ margin: v })}
                          testIdPrefix="margin"
                        />
                      </LabelField>
                      <LabelField label="Page size">
                        <Segmented
                          value={style.page_size}
                          options={[{ value: 'letter', label: 'Letter' }, { value: 'a4', label: 'A4' }]}
                          onChange={(v) => patchStyle({ page_size: v })}
                          testIdPrefix="page-size"
                        />
                      </LabelField>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Footer controls */}
          <div className="p-4 flex items-center gap-2 text-xs">
            <button
              onClick={resetStyle}
              className="flex items-center gap-1 text-[#6b7280] hover:text-[#0f1115]"
              data-testid="studio-reset"
            >
              <RotateCcw size={12} /> Reset to defaults
            </button>
          </div>

          {/* ATS safety hint */}
          <div className="m-4 p-3 bg-[#ecfdf5] border border-[#10b981] text-xs text-[#065f46] flex gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div><strong>ATS-safe always.</strong> Every theme uses single-column layout, semantic headings, and parser-friendly fonts. No configuration can break your ATS score.</div>
          </div>
        </aside>

        {/* Live preview */}
        <main className="flex-1 overflow-auto bg-[#e5e7eb]" data-testid="studio-preview">
          <iframe
            ref={previewFrame}
            title="Resume live preview"
            srcDoc={previewHtml}
            className="w-full h-full border-0"
            data-testid="studio-preview-iframe"
          />
        </main>
      </div>
    </div>
  );
};

// --- Helpers ----------------------------------------------------------------

const LabelField = ({ label, children }) => (
  <div>
    <div className="text-xs font-semibold text-[#4b5563] mb-1.5 uppercase tracking-wider">{label}</div>
    {children}
  </div>
);

const Select = ({ value, options, onChange, testId }) => (
  <select
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value || null)}
    className="w-full px-3 py-1.5 text-sm border border-[#e5e7eb] bg-white focus:border-[#002fa7] focus:outline-none"
    data-testid={testId}
  >
    {options.map((opt) => (
      <option key={opt.label} value={opt.value ?? ''}>{opt.label}</option>
    ))}
  </select>
);

// Best-effort markdown reconstruction from the edited preview DOM. Enough
// to round-trip simple text edits — for structural changes users should use
// the main editor. Kept intentionally simple.
const serializePreviewToMarkdown = (paperEl) => {
  const parts = [];
  const name = paperEl.querySelector('.name')?.textContent?.trim();
  if (name) parts.push(name);
  const contacts = Array.from(paperEl.querySelectorAll('.contact span'))
    .filter((s) => !s.classList.contains('sep'))
    .map((s) => s.textContent.trim())
    .filter(Boolean);
  if (contacts.length) parts.push(contacts.join(' | '));
  parts.push('');

  paperEl.querySelectorAll('section.section').forEach((sec) => {
    const h = sec.querySelector('h2')?.textContent?.trim();
    if (h) parts.push(`## ${h}`);
    Array.from(sec.children).forEach((child) => {
      if (child.tagName === 'H2') return;
      if (child.tagName === 'UL') {
        child.querySelectorAll('li').forEach((li) =>
          parts.push(`- ${li.textContent.trim()}`)
        );
      } else if (child.tagName === 'P') {
        // Preserve pipe-separated role subheadings as they were
        const text = child.textContent.trim().replace(/\s+/g, ' ');
        if (text) parts.push(text);
      }
    });
    parts.push('');
  });
  return parts.join('\n').replace(/\n{3,}/g, '\n\n').trim();
};

const timeAgo = (date) => {
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 2) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return date.toLocaleTimeString();
};

export default ResumeStudio;
